import React, { lazy, useState, useContext, useMemo, useEffect } from "react";
import { Breadcrumbs, BreadcrumbItem } from "@vds/breadcrumbs";
import { Spacer, SLNotification } from "../../components/core/utility";
import { handleChangePage, switchTab } from "./utils";
import styled from "styled-components";
import { OrderShippingSummaryAccordion } from "../C4S Decommission - Replacement/OrderShippingSummaryAccordion";
import CheckIcon from "../../assets/icons/check.svg";
import { Button } from "@vds/buttons";
import { assignMaterialVehicleDetailsList } from "../../services/C4S Decommission - Replacement/assignMaterialVehicleDetails";
import { AssignMaterialVehicleGrid } from "./AssignMaterialVehicleGrid";
import { Loader } from "@vds/loaders";
import WTCommonModal from "./WTCommonModal";
import { C4SContext } from "../../context/c4sContext";
import { generate } from "shortid";
import useOutsideClick from "../../customHook/use-outside-click";
import ConfirmationModal from "./ConfirmationModal";
import ReturnLabelContactModal from "./ReturnLabelContactModal";
import { partialReturnReship } from "../../services/C4S Decommission - Replacement/partialReurnReship";
import moment from "moment";
import { getReturnCode } from "../../../constants/ProcessingCodeConstants";
import { messageBus } from "../../../utility/messageBus";
import ReturnReshipSummaryComponent from "./ReturnReshipSummaryComponent";

const BundleDropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 680px; /* fixed width instead of min-width */
`;

const BundleDropdownTrigger = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: 1px solid #888;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BundleOptionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow: auto;
  border: 1px solid #ccc;
  background: #fff;
  z-index: 20;
  border-radius: 4px;
  padding: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const BundleOptionRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: flex-start; /* left align contents */
  gap: 0.5rem;
  font-family: Verizon-NHG-eDS;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  margin-left: 10px; /* slight left indent */
  padding: 0.25rem 0.25rem; /* uniform padding */
  text-align: left;
  cursor: pointer;
  &:hover {
    background: #f0f8ff;
  }
`;

const SelectedMaterialLabel = styled.span`
  font-family: Verizon-NHG-eDS;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const AssignMaterialComponent = (props) => {
  const sampleBundles = props.selectedMaterials;
  const aggregatedMaterials = useMemo(() => {
    const aggregate = {};
    sampleBundles.forEach((rec) => {
      const comboKey = `${rec.hardwareId}|${rec.subscriptionId}`;
      if (!aggregate[comboKey]) {
        aggregate[comboKey] = {
          hardwareId: rec.hardwareId,
          hardwareName: rec.hardwareName,
          subscriptionId: rec.subscriptionId,
          subscriptionName: rec.subscriptionName,
          techRefId: rec.techRefId,
          requestedQuantity: 0,
          bundleIds: new Set(),
          workticketIds: new Set(),
          esns: new Set(),
        };
      }
      aggregate[comboKey].requestedQuantity += 1;
      if (rec.bundleId) aggregate[comboKey].bundleIds.add(rec.bundleId);
      if (rec.workTicketId) aggregate[comboKey].workticketIds.add(rec.workTicketId);
      if (rec.esn) aggregate[comboKey].esns.add(rec.esn);
    });
    return Object.values(aggregate).map((m) => ({
      ...m,
      bundleIds: Array.from(m.bundleIds),
      workticketIds: Array.from(m.workticketIds),
      esns: Array.from(m.esns),
    }));
  }, [sampleBundles]);

  // Quick lookup map by key (hardwareId|subscriptionId)
  const materialsByKey = useMemo(() => {
    const map = {};
    aggregatedMaterials.forEach((m) => {
      map[`${m.hardwareId}|${m.subscriptionId}`] = m;
    });
    return map;
  }, [aggregatedMaterials]);

  // First key from flat list
  const firstKey = useMemo(() => {
    return aggregatedMaterials.length ? `${aggregatedMaterials[0].hardwareId}|${aggregatedMaterials[0].subscriptionId}` : "";
  }, [aggregatedMaterials]);

  const [selectedHardwareKey, setSelectedHardwareKey] = useState(firstKey);
  const [isBundleDropdownOpen, setIsBundleDropdownOpen] = useState(false);
  const [vehicleList, setVehicleList] = useState([]);
  const [isVehicleLoading, setIsVehicleLoading] = useState(false);
  const [activeBundleId, setActiveBundleId] = useState("");
  const [combinedVehiclesByBundle, setCombinedVehiclesByBundle] = useState({});
  const [selectedMaterialDetails, setSelectedMaterialDetails] = useState(null);
  const [materialSelections, setMaterialSelections] = useState({});
  const [savedMaterialKeys, setSavedMaterialKeys] = useState({});
  const [materialVehiclesCache, setMaterialVehiclesCache] = useState({});
  const [selectedVehicleEntities, setSelectedVehicleEntities] = useState({});
  const [returnPayload, setReturnPayload] = useState(null);
  const [actionName, setActionName] = useState("");
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryBundleMap, setSummaryBundleMap] = useState({});

  const currentMaterialKey = selectedHardwareKey;

  useEffect(() => {
    setMaterialSelections((prev) => {
      const next = { ...prev };
      aggregatedMaterials.forEach((item) => {
        const key = `${item.hardwareId}|${item.subscriptionId}`;
        const related = sampleBundles.filter((rec) => rec.hardwareId === item.hardwareId && rec.subscriptionId === item.subscriptionId);
        const addressIds = related.map((r) => r.address?.addressId).filter(Boolean);
        const contactIds = related.map((r) => r.contact?.contactId).filter(Boolean);
        const workticketIds = related.map((r) => r.workTicketId).filter(Boolean);
        const esns = related.map((r) => r.esn).filter(Boolean);
        if (!next[key]) {
          next[key] = {
            requestedQuantity: item.requestedQuantity,
            vehicleIds: [],
            hardwareId: item.hardwareId,
            subscriptionId: item.subscriptionId,
            workticketId: workticketIds[0] || "",
            esn: esns[0] || "",
            addressId: addressIds[0] || "",
            contactId: contactIds[0] || "",
          };
        } else {
          next[key] = {
            ...next[key],
            requestedQuantity: item.requestedQuantity,
            hardwareId: item.hardwareId,
            subscriptionId: item.subscriptionId,
            workticketId: next[key].workticketId || workticketIds[0] || "",
            esn: next[key].esn || esns[0] || "",
            addressId: next[key].addressId || addressIds[0] || "",
            contactId: next[key].contactId || contactIds[0] || "",
          };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregatedMaterials]);

  const computeTempVehicleKey = (v, hardwareId, subscriptionId) => {
    const base = v?.vin || v?.vehicle || v?.id || v?.serial || `${v?.year || ""}-${v?.make || ""}-${v?.model || ""}`;
    return `${base}-${hardwareId || ""}-${subscriptionId || ""}`;
  };

  const handleVehicleSelectionChange = ({ materialKey, vehicleIds }) => {
    const [hardwareId, subscriptionId] = materialKey.split("|");
    const rvFiltered = sampleBundles.filter((rec) => rec.hardwareId === hardwareId && rec.subscriptionId === subscriptionId);
    const rvAddressId = rvFiltered.find((r) => r.shippingAddress?.addressId)?.shippingAddress?.addressId || "";
    const rvContactId = rvFiltered.find((r) => r.contact?.contactId)?.contact?.contactId || "";
    const workticketId = rvFiltered.find((r) => r.workticketId)?.workticketId || "";
    const esn = rvFiltered.find((r) => r.esn)?.esn || "";
    setMaterialSelections((prev) => ({
      ...prev,
      [materialKey]: {
        ...(prev[materialKey] || { requestedQuantity: rvFiltered.length, vehicleIds: [] }),
        requestedQuantity: prev[materialKey]?.requestedQuantity || rvFiltered.length || 0,
        vehicleIds,
        hardwareId,
        subscriptionId,
        workticketId: prev[materialKey]?.workticketId || workticketId,
        esn: prev[materialKey]?.esn || esn,
        addressId: prev[materialKey]?.addressId || rvAddressId,
        contactId: prev[materialKey]?.contactId || rvContactId,
      },
    }));
    const allVehicles = [];
    if (combinedVehiclesByBundle && typeof combinedVehiclesByBundle === "object" && !Array.isArray(combinedVehiclesByBundle)) {
      Object.entries(combinedVehiclesByBundle).forEach(([bundleId, bundleGroups]) => {
        if (Array.isArray(bundleGroups)) {
          bundleGroups.forEach((g) => {
            if (g && Array.isArray(g.vehicles)) {
              g.vehicles.forEach((veh) => {
                const candidates = sampleBundles.filter(
                  (rec) => rec.bundleId === bundleId && rec.hardwareId === hardwareId && rec.subscriptionId === subscriptionId
                );
                let derivedWT = "";
                if (veh.esn && candidates.length) {
                  const esnMatch = candidates.find((c) => c.esn === veh.esn);
                  if (esnMatch) derivedWT = esnMatch.workTicketId;
                }
                if (!derivedWT && candidates.length) derivedWT = candidates[0].workTicketId;
                const enriched = {
                  ...veh,
                  bundleId,
                  hardwareId,
                  subscriptionId,
                  workticketId: derivedWT,
                  _stableId: computeTempVehicleKey(veh, hardwareId, subscriptionId),
                  addressId: g.address?.addressId || rvAddressId,
                  contactId: g.contact?.contactId || rvContactId,
                };
                allVehicles.push(enriched);
              });
            }
          });
        }
      });
    } else if (Array.isArray(vehicleList)) {
      vehicleList.forEach((v) => {
        const enriched = {
          ...v,
          hardwareId,
          subscriptionId,
          _stableId: computeTempVehicleKey(v, hardwareId, subscriptionId),
          addressId: rvAddressId,
          contactId: rvContactId,
        };
        const candidates = sampleBundles.filter((rec) => rec.hardwareId === hardwareId && rec.subscriptionId === subscriptionId);
        let derivedWT = "";
        if (v.esn && candidates.length) {
          const esnMatch = candidates.find((c) => c.esn === v.esn);
          if (esnMatch) derivedWT = esnMatch.workTicketId;
        }
        if (!derivedWT && candidates.length) derivedWT = candidates[0].workTicketId;
        enriched.workticketId = derivedWT;
        allVehicles.push(enriched);
      });
    }
    const vehicleIdSet = new Set(vehicleIds);
    const matched = allVehicles.filter((v) => vehicleIdSet.has(v._stableId) || vehicleIdSet.has(v.id) || vehicleIdSet.has(v.vehicle));
    setSelectedVehicleEntities((prev) => ({ ...prev, [materialKey]: matched }));
  };

  const isSaveEnabled = (() => {
    if (!currentMaterialKey) return false;
    const rec = materialSelections[currentMaterialKey];
    const alreadySaved = savedMaterialKeys[currentMaterialKey];
    if (!rec || alreadySaved) return false;
    return rec.vehicleIds.length === rec.requestedQuantity && rec.requestedQuantity > 0;
  })();

  const overSelectedInfo = (() => {
    if (!currentMaterialKey) return null;
    const rec = materialSelections[currentMaterialKey];
    const alreadySaved = savedMaterialKeys[currentMaterialKey];
    if (!rec || alreadySaved) return null;
    if (rec.vehicleIds.length > rec.requestedQuantity) {
      return {
        selected: rec.vehicleIds.length,
        requested: rec.requestedQuantity,
      };
    }
    return null;
  })();

  const isContinueEnabled = (() => {
    const entries = Object.keys(materialSelections);
    if (!entries.length) return false;
    return entries.every((key) => savedMaterialKeys[key]);
  })();
  const { notification } = useContext(C4SContext);
  const [commonModalStatus, setCommonModalStatus] = useState(false);
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [actionItem, setActionItem] = useState("");
  const [showReturnLabelModal, setShowReturnLabelModal] = useState(false);
  const cspDetails = useMemo(() => {
    try {
      const details = sessionStorage.getItem("CSPDetails");
      return details ? JSON.parse(details) : null;
    } catch (error) {
      notify("Failed to parse CSPDetails from sessionStorage", "error");
      return null; // Fallback to null on parsing error
    }
  }, []);
  const [reason, setReason] = useState("");
  const [subReason, setSubReason] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const salesOrgId = cspDetails?.CSP?.length ? cspDetails?.CSP.filter((csp) => csp.DefaultCSP === "1")[0].Opp_Sales_Org : "";

  const toggleBundleDropdown = () => setIsBundleDropdownOpen((o) => !o);

  const handleHardwareSelect = async (key) => {
    setSelectedHardwareKey(key);
    setIsBundleDropdownOpen(false);

    const found = materialsByKey[key];
    const primaryBundleId = found?.bundleIds?.[0] || "";
    setActiveBundleId(primaryBundleId);
    setSelectedMaterialDetails(found ? { ...found } : null);

    if (savedMaterialKeys[key] && materialVehiclesCache[key]) {
      setCombinedVehiclesByBundle(materialVehiclesCache[key]);
      setVehicleList(materialVehiclesCache[key]);
      return;
    }

    try {
      setIsVehicleLoading(true);
      setVehicleList([]);
      if (!primaryBundleId) {
        setIsVehicleLoading(false);
        return;
      }

      const payload = {
        hardwareId: found?.hardwareId || "",
        subscriptionId: found?.subscriptionId || "",
        techRefId: found?.techRefId || "",
        workticketId: Array.isArray(found?.workticketIds) ? found.workticketIds.join(",") : "",
        esn: Array.isArray(found?.esns) ? found.esns.join(",") : "",
      };

      const response = await assignMaterialVehicleDetailsList(payload);

      if (response?.status === 200 && response?.data?.data) {
        const rawDataArray = response.data.data;
        const bundleMap = {};
        if (Array.isArray(rawDataArray)) {
          rawDataArray.forEach((entryObj) => {
            if (entryObj && typeof entryObj === "object") {
              Object.keys(entryObj).forEach((bundleIdKey) => {
                const groups = entryObj[bundleIdKey];
                if (Array.isArray(groups)) {
                  bundleMap[bundleIdKey] = groups.map((g) => ({
                    address: g.address || {},
                    contact: g.contact || {},
                    vehicles: Array.isArray(g.vehicles) ? g.vehicles : [],
                  }));
                } else {
                  bundleMap[bundleIdKey] = [];
                }
              });
            }
          });
        }
        setCombinedVehiclesByBundle(bundleMap);
        setVehicleList(bundleMap);
        setMaterialVehiclesCache((prev) => ({ ...prev, [key]: bundleMap }));
      } else if (response?.status === 200 && !response?.data?.data) {
        setVehicleList(null);
        setCombinedVehiclesByBundle({});
        notify("No data to display", "error");
      } else {
        setVehicleList(null);
        setCombinedVehiclesByBundle({});
        notify("Something went wrong, please try again.", "error");
      }
    } catch (e) {
      setVehicleList([]);
      notify("Something went wrong, please try again.", "error");
    } finally {
      setIsVehicleLoading(false);
    }
  };

  const handleSaveAssignment = () => {
    if (!isSaveEnabled || !currentMaterialKey) return;
    setActionName("assignment");
    setIsConfirmationModal(true);
  };

  const handleContinueAssigment = () => {
    if (!currentMaterialKey) {
      setIsConfirmationModal(false);
      return;
    }
    if (isSaveEnabled && !savedMaterialKeys[currentMaterialKey]) {
      setSavedMaterialKeys((prev) => ({ ...prev, [currentMaterialKey]: true }));
      if (!materialVehiclesCache[currentMaterialKey]) {
        materialVehiclesCache[currentMaterialKey] = combinedVehiclesByBundle;
      }
    }
    setIsConfirmationModal(false);
  };

  const handleClickOutside = () => {
    notification.setNotification(null);
  };

  const clickRef = useOutsideClick(handleClickOutside);

  const notify = (content, type) => {
    notification.setNotification(<SLNotification key={generate()} type={type} title={content} fullBleed={true} ref={clickRef} />);
  };

  const handleContinueAll = () => {
    if (isContinueEnabled) {
      const returnsEntries = [];
      Object.keys(savedMaterialKeys).forEach((materialKey) => {
        if (!savedMaterialKeys[materialKey]) return;
        const [hardwareId, subscriptionId] = materialKey.split("|");
        const materialMeta = materialsByKey[materialKey];
        const relatedRecords = sampleBundles.filter((rec) => rec.hardwareId === hardwareId && rec.subscriptionId === subscriptionId);
        const selectedVehs = selectedVehicleEntities[materialKey] || [];
        if (!relatedRecords.length || !selectedVehs.length) return;

        const workticketCounts = {};
        relatedRecords.forEach((r) => {
          if (r.workTicketId) {
            r.workTicketId.split(",").forEach((wt) => {
              const trimmed = wt.trim();
              if (trimmed) workticketCounts[trimmed] = (workticketCounts[trimmed] || 0) + 1;
            });
          }
        });

        const workticketIds = Object.keys(workticketCounts);
        if (!workticketIds.length) return;

        const getVid = (v) => v.id || v._stableId || v.vehicle || computeTempVehicleKey(v, hardwareId, subscriptionId);
        const vehiclesPool = [...selectedVehs];

        const includesWorkticket = (val, wtId) => {
          if (!val) return false;
          return val
            .split(",")
            .map((x) => x.trim())
            .includes(wtId);
        };

        const hasExactMapping = (v, hw, sub, wt) => {
          return (
            Array.isArray(v.materialMapping) &&
            v.materialMapping.some((m) => m && m.hardwareId === hw && m.subscriptionId === sub && includesWorkticket(m.workticketId, wt))
          );
        };

        const allocateForWorkticket = (wtId, needed) => {
          if (needed <= 0) return [];
          const chosen = [];
          for (let i = 0; i < vehiclesPool.length && chosen.length < needed; i += 1) {
            const v = vehiclesPool[i];
            if (hasExactMapping(v, hardwareId, subscriptionId, wtId)) {
              chosen.push(v);
            }
          }
          const chosenIds = new Set(chosen.map((c) => getVid(c)));
          for (let i = vehiclesPool.length - 1; i >= 0; i -= 1) {
            const vid = getVid(vehiclesPool[i]);
            if (chosenIds.has(vid)) vehiclesPool.splice(i, 1);
          }
          return chosen;
        };

        workticketIds.forEach((wtId) => {
          const needed = workticketCounts[wtId] || 0;
          const allocatedVehs = allocateForWorkticket(wtId, needed);
          if (!allocatedVehs.length) return;

          const subsetRecords = relatedRecords.filter((r) => includesWorkticket(r.workTicketId, wtId));
          const deliverySet = new Set();
          subsetRecords.forEach((r) => {
            if (r.oldDeliveryNum) {
              r.oldDeliveryNum.split(",").forEach((d) => {
                const trimmed = d.trim();
                if (trimmed) deliverySet.add(trimmed);
              });
            }
          });
          const oldDeliveryNo = Array.from(deliverySet).join(",");
          const exemplar = subsetRecords[0] || relatedRecords[0] || {};
          const returnProcessingCode = getReturnCode(exemplar.processingCode || materialMeta?.processingCode || "");
          const addrSet = new Set(allocatedVehs.map((v) => v.addressId).filter(Boolean));
          const contactSet = new Set(allocatedVehs.map((v) => v.contactId).filter(Boolean));
          const derivedAddressId = addrSet.size ? Array.from(addrSet)[0] : subsetRecords.find((r) => r.address?.addressId)?.address?.addressId || "";
          const derivedContactId = contactSet.size
            ? Array.from(contactSet)[0]
            : subsetRecords.find((r) => r.contact?.contactId)?.contact?.contactId || "";
          const materialLineId = "000010";
          const materialObj = {
            id: Number(exemplar.workticketMaterialIdKey) || exemplar.workticketMaterialIdKey || null,
            materialId: hardwareId,
            esnTobeReturned: (() => {
              const esnObjects = [];
              const seen = new Set();
              subsetRecords
                .filter(
                  (r) => r.esn && r.hardwareId === hardwareId && r.subscriptionId === subscriptionId && includesWorkticket(r.workTicketId, wtId)
                )
                .forEach((r, idx) => {
                  if (!seen.has(r.esn)) {
                    seen.add(r.esn);
                    esnObjects.push({ esnId: r.esnId || Number(r.esnId) || Number(r.id) || idx + 1, esn: r.esn });
                  }
                });
              return esnObjects;
            })(),
            summaryQty: allocatedVehs.length,
            materialLineId,
            returnProcessingCode,
            vins: allocatedVehs.map((v) => {
              const vid = getVid(v);
              return {
                id: vid,
                vinStatus: v.vehicleStatus || "Ready",
                type: "VEHICLE",
                vinMaterials: (() => {
                  if (Array.isArray(v.materialMapping)) {
                    const mmExact = v.materialMapping.find(
                      (m) => m && m.hardwareId === hardwareId && m.subscriptionId === subscriptionId && includesWorkticket(m.workticketId, wtId)
                    );
                    if (mmExact) return mmExact.vinMaterials || mmExact.id || "";
                  }
                  return "";
                })(),
              };
            }),
          };
          returnsEntries.push({
            workticketId: wtId,
            oldDeliveryNo,
            addressId: derivedAddressId,
            contactId: derivedContactId,
            materials: [materialObj],
          });
        });
      });
      const mergedMap = {};
      returnsEntries.forEach((entry) => {
        const key = entry.workticketId;
        if (!mergedMap[key]) {
          mergedMap[key] = { ...entry, materials: [...entry.materials] };
        } else {
          const existingDelSet = new Set(
            (mergedMap[key].oldDeliveryNo || "")
              .split(",")
              .map((d) => d.trim())
              .filter(Boolean)
          );
          (entry.oldDeliveryNo || "")
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean)
            .forEach((d) => existingDelSet.add(d));
          mergedMap[key].oldDeliveryNo = Array.from(existingDelSet).join(",");
          mergedMap[key].materials = [...mergedMap[key].materials, ...entry.materials];
        }
      });
      Object.values(mergedMap).forEach((entry) => {
        entry.materials.forEach((mat, idx) => {
          const lineNumber = ((idx + 1) * 10).toString().padStart(6, "0");
          mat.materialLineId = lineNumber;
        });
      });
      const mergedReturns = Object.values(mergedMap);
      const built = { returns: mergedReturns };
      setReturnPayload(built);

      const getVehId = (v) => v?.id || v?._stableId || v?.vehicle || v?.vin;
      const selectedIdSet = new Set();
      Object.keys(savedMaterialKeys).forEach((mk) => {
        if (!savedMaterialKeys[mk]) return;
        (selectedVehicleEntities[mk] || []).forEach((v) => {
          const vid = getVehId(v);
          if (vid) selectedIdSet.add(vid);
        });
      });

      const mergedByBundle = {};
      const addVehicleToGroup = (bundleId, addressObj, contactObj, veh) => {
        const addrObj = addressObj || {};
        const contObj = contactObj || {};
        const vid = getVehId(veh);
        if (!vid || !selectedIdSet.has(vid)) return;
        if (!mergedByBundle[bundleId]) mergedByBundle[bundleId] = new Map();
        const addrKey = addrObj.addressId || addrObj.address1 || JSON.stringify(addrObj);
        if (!mergedByBundle[bundleId].has(addrKey)) {
          mergedByBundle[bundleId].set(addrKey, { address: addrObj, contact: contObj, vehicles: [], _seen: new Set() });
        }
        const group = mergedByBundle[bundleId].get(addrKey);
        if (!group._seen.has(vid)) {
          group._seen.add(vid);
          group.vehicles.push(veh);
        }
      };

      Object.keys(savedMaterialKeys).forEach((mk) => {
        if (!savedMaterialKeys[mk]) return;
        const src = materialVehiclesCache[mk] || {};
        Object.entries(src).forEach(([bundleId, groupsArr]) => {
          if (!Array.isArray(groupsArr)) return;
          groupsArr.forEach((g) => {
            const addr = g?.address || {};
            const contact = g?.contact || {};
            (g?.vehicles || []).forEach((veh) => addVehicleToGroup(bundleId, addr, contact, veh));
          });
        });
      });

      const summaryMap = {};
      Object.entries(mergedByBundle).forEach(([bundleId, addrMap]) => {
        summaryMap[bundleId] = Array.from(addrMap.values()).map((g) => ({ address: g.address, contact: g.contact, vehicles: g.vehicles }));
      });

      const hasAnySelected = Object.keys(summaryMap).some((b) => (summaryMap[b] || []).some((grp) => (grp.vehicles || []).length));
      setSummaryBundleMap(hasAnySelected ? summaryMap : combinedVehiclesByBundle);

      setShowSummary(true);
    }
    setTitle("Select Reason for Return/Reship or Return");
    setSubTitle("Select Reason");
    setActionItem(props.actionName);
  };

  const handleSummaryBack = () => {
    setShowSummary(false);
  };
  const handleSummaryContinue = (opts) => {
    const disabledKeys = Array.isArray(opts?.disabledKeys) ? new Set(opts.disabledKeys) : new Set();

    if (disabledKeys.size > 0) {
      const returnsEntries = [];

      Object.keys(savedMaterialKeys).forEach((materialKey) => {
        if (!savedMaterialKeys[materialKey]) return;
        const [hardwareId, subscriptionId] = materialKey.split("|");
        const materialMeta = materialsByKey[materialKey];
        const relatedRecords = sampleBundles.filter((rec) => rec.hardwareId === hardwareId && rec.subscriptionId === subscriptionId);
        const selectedVehs = selectedVehicleEntities[materialKey] || [];
        if (!relatedRecords.length || !selectedVehs.length) return;

        const workticketCounts = {};
        relatedRecords.forEach((r) => {
          if (r.workTicketId) {
            r.workTicketId.split(",").forEach((wt) => {
              const trimmed = wt.trim();
              if (trimmed) workticketCounts[trimmed] = (workticketCounts[trimmed] || 0) + 1;
            });
          }
        });

        const workticketIds = Object.keys(workticketCounts);
        if (!workticketIds.length) return;

        const getVid = (v) => v.id || v._stableId || v.vehicle || `${v?.vin || ""}`;
        const vehiclesPool = [...selectedVehs];
        const includesWorkticket = (val, wtId) => {
          if (!val) return false;
          return val
            .split(",")
            .map((x) => x.trim())
            .includes(wtId);
        };
        const isDisabledMapping = (veh, m) => {
          const vid = getVid(veh);
          const hw = m?.hardwareId || "";
          const sub = m?.subscriptionId || "";
          const wt = (m?.workticketId || m?.workTicketId || "").toString();
          const key = `${vid}|${hw}|${sub}|${wt}`;
          return disabledKeys.has(key);
        };
        const hasExactEnabledMapping = (v, hw, sub, wt) => {
          return (
            Array.isArray(v.materialMapping) &&
            v.materialMapping.some((m) => {
              return m && m.hardwareId === hw && m.subscriptionId === sub && includesWorkticket(m.workticketId, wt) && !isDisabledMapping(v, m);
            })
          );
        };
        const allocateForWorkticket = (wtId, needed) => {
          if (needed <= 0) return [];
          const chosen = [];
          for (let i = 0; i < vehiclesPool.length && chosen.length < needed; i += 1) {
            const v = vehiclesPool[i];
            if (hasExactEnabledMapping(v, hardwareId, subscriptionId, wtId)) {
              chosen.push(v);
            }
          }
          const chosenIds = new Set(chosen.map((c) => getVid(c)));
          for (let i = vehiclesPool.length - 1; i >= 0; i -= 1) {
            const vid = getVid(vehiclesPool[i]);
            if (chosenIds.has(vid)) vehiclesPool.splice(i, 1);
          }
          return chosen;
        };

        workticketIds.forEach((wtId) => {
          const needed = workticketCounts[wtId] || 0;
          const allocatedVehs = allocateForWorkticket(wtId, needed);
          if (!allocatedVehs.length) return;

          const subsetRecords = relatedRecords.filter((r) => includesWorkticket(r.workTicketId, wtId));
          const deliverySet = new Set();
          subsetRecords.forEach((r) => {
            if (r.oldDeliveryNum) {
              r.oldDeliveryNum.split(",").forEach((d) => {
                const trimmed = d.trim();
                if (trimmed) deliverySet.add(trimmed);
              });
            }
          });
          const oldDeliveryNo = Array.from(deliverySet).join(",");
          const exemplar = subsetRecords[0] || relatedRecords[0] || {};
          const returnProcessingCode = getReturnCode(exemplar.processingCode || materialMeta?.processingCode || "");
          const addrSet = new Set(allocatedVehs.map((v) => v.addressId).filter(Boolean));
          const contactSet = new Set(allocatedVehs.map((v) => v.contactId).filter(Boolean));
          const derivedAddressId = addrSet.size ? Array.from(addrSet)[0] : subsetRecords.find((r) => r.address?.addressId)?.address?.addressId || "";
          const derivedContactId = contactSet.size
            ? Array.from(contactSet)[0]
            : subsetRecords.find((r) => r.contact?.contactId)?.contact?.contactId || "";
          const materialLineId = "000010";
          const materialObj = {
            id: Number(exemplar.workticketMaterialIdKey) || exemplar.workticketMaterialIdKey || null,
            materialId: hardwareId,
            esnTobeReturned: (() => {
              const esnObjects = [];
              const seen = new Set();
              subsetRecords
                .filter((r) => {
                  return r.esn && r.hardwareId === hardwareId && r.subscriptionId === subscriptionId && includesWorkticket(r.workTicketId, wtId);
                })
                .forEach((r, idx) => {
                  if (!seen.has(r.esn)) {
                    seen.add(r.esn);
                    esnObjects.push({ esnId: r.esnId || Number(r.esnId) || Number(r.id) || idx + 1, esn: r.esn });
                  }
                });
              return esnObjects;
            })(),
            summaryQty: allocatedVehs.length,
            materialLineId,
            returnProcessingCode,
            vins: allocatedVehs.map((v) => {
              const vid = getVid(v);
              return {
                id: vid,
                vinStatus: v.vehicleStatus || "Ready",
                type: "VEHICLE",
                vinMaterials: (() => {
                  if (Array.isArray(v.materialMapping)) {
                    const mmExact = v.materialMapping.find((m) => {
                      return (
                        m &&
                        m.hardwareId === hardwareId &&
                        m.subscriptionId === subscriptionId &&
                        includesWorkticket(m.workticketId, wtId) &&
                        !isDisabledMapping(v, m)
                      );
                    });
                    if (mmExact) return mmExact.vinMaterials || mmExact.id || "";
                  }
                  return "";
                })(),
              };
            }),
          };
          returnsEntries.push({
            workticketId: wtId,
            oldDeliveryNo,
            addressId: derivedAddressId,
            contactId: derivedContactId,
            materials: [materialObj],
          });
        });
      });

      const mergedMap = {};
      returnsEntries.forEach((entry) => {
        const key = entry.workticketId;
        if (!mergedMap[key]) {
          mergedMap[key] = { ...entry, materials: [...entry.materials] };
        } else {
          const existingDelSet = new Set(
            (mergedMap[key].oldDeliveryNo || "")
              .split(",")
              .map((d) => d.trim())
              .filter(Boolean)
          );
          (entry.oldDeliveryNo || "")
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean)
            .forEach((d) => existingDelSet.add(d));
          mergedMap[key].oldDeliveryNo = Array.from(existingDelSet).join(",");
          mergedMap[key].materials = [...mergedMap[key].materials, ...entry.materials];
        }
      });
      Object.values(mergedMap).forEach((entry) => {
        entry.materials.forEach((mat, idx) => {
          const lineNumber = ((idx + 1) * 10).toString().padStart(6, "0");
          mat.materialLineId = lineNumber;
        });
      });
      const finalReturns = Object.values(mergedMap);
      setReturnPayload({ returns: finalReturns });
    }

    setCommonModalStatus(true);
  };

  const handleContinue = () => {
    if (actionName === "contact") {
      setIsConfirmationModal(false);
      setShowReturnLabelModal(true);
    } else if (actionName === "create_workticket") {
      setIsConfirmationModal(false);
      handlePartialReturnReship();
    }
  };

  const handlePartialReturnReship = async () => {
    setIsVehicleLoading(true);
    const payload = {
      action: props.actionName === "return" ? "Return" : "Reship",
      createdBy: `${cspDetails?.firstName} ${cspDetails?.lastName}`,
      createdByEmail: cspDetails?.email,
      createdByEid: cspDetails?.Employee_ID,
      accountName: cspDetails?.AccountName,
      assignTo: cspDetails?.Employee_ID,
      assignedTo: cspDetails.CSP[0].CSPOwner,
      assignedToEmail: cspDetails.CSP[0].CSPOwnerEmail,
      assignedToEid: cspDetails.CSP[0].CSPOwnerEId,
      erpProductRecipientPartyId: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number,
      erpBusinessPartnerId: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number,
      resellerBusinessPartnerNumber: cspDetails?.ResellerBusinessPartnerNumber || cspDetails?.ERP_Partner_Number || "", // ResellerBusinessPartnerNumber
      erpTicketType: cspDetails?.CSP[0].CSPID !== "" ? "ZINS" : "ZSRQ",
      distributionChannel: cspDetails?.distributionChannel || "02",
      division: cspDetails?.division || "02",
      requestDate: moment().format("YYYYMMDD"),
      crmReferenceId: cspDetails?.CSP?.[0]?.CRM_Ref_ID || "",
      documentType: cspDetails?.CSP[0].CSPID !== "" ? "ZINS" : "ZSRQ",
      documentDate: moment().format("YYYYMMDD"),
      salesOrg: salesOrgId ? salesOrgId : "",
      contractAccount: cspDetails?.CONTRACT_ACCOUNT_ID?.[0] || "",
      erpPrimaryContactNumber: cspDetails?.ERP_Primary_Contact_NUmber ?? "",
      cspId: cspDetails.CSP[0].CSPID,
      returnLabelName: name ? name : "",
      returnLabelPhone: phone ? phone : "",
      returnLabelEmail: email ? email : "",
      returnReason: reason ? reason : "",
      returnSubReason: subReason ? subReason : "",
      returns: returnPayload.returns,
    };
    const response = await partialReturnReship(payload);
    if (response?.status === 200 && response?.data) {
      notify("Return processed successfully", "success");
      switchTab(3);
    } else {
      notify("Unable to process return due to an unexpected error", "error");
    }
    setIsVehicleLoading(false);
    setTimeout(() => {
      notification.setNotification(null);
    }, 3000);
  };

  const handleContactModal = (reasonDescription, value) => {
    if (value === "reason") {
      setReason(reasonDescription);
      setSubReason("");
    } else if (value === "subReason") {
      setSubReason(reasonDescription);
    }
    setActionName("contact");
    setIsConfirmationModal(true);
  };

  useEffect(() => {
    if (selectedHardwareKey && vehicleList.length === 0) {
      handleHardwareSelect(selectedHardwareKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setShowReturnLabelModal(false);
  };

  const handleSaveData = (name, phone, email) => {
    setName(name);
    setPhone(phone);
    setEmail(email);
    setShowReturnLabelModal(false);
    setActionName("create_workticket");
    setIsConfirmationModal(true);
  };

  const handleNo = () => {
    setName("");
    setPhone("");
    setEmail("");
    setIsConfirmationModal(false);
    setActionName("create_workticket");
    setIsConfirmationModal(true);
  };

  const handleShipmentDamagedModal = (reasonDescription, value) => {
    if (value === "reason") {
      setReason(reasonDescription);
    }
    setTitle("Who Damaged/Lost the Package?");
    setSubTitle("Select Who Damaged/Lost the Package");
    setActionItem("Damaged_Lost_Package");
    setCommonModalStatus(true);
  };

  const handleBackClick = () => {
    setActionName("back");
    setIsConfirmationModal(true);
  };

  const handleBackContinue = () => {
    setMaterialSelections({});
    setSavedMaterialKeys({});
    setSelectedVehicleEntities({});
    setMaterialVehiclesCache({});
    setSelectedHardwareKey("");
    setVehicleList([]);
    setCombinedVehiclesByBundle({});
    setActiveBundleId("");
    setSelectedMaterialDetails(null);
    try {
      messageBus?.trigger && messageBus.trigger("resetFilters");
    } catch (e) {
      // no-op if messageBus unavailable
    }
    setIsConfirmationModal(false);
    handleChangePage({ index: 0 });
  };

  return (
    <div>
      {showSummary ? (
        <ReturnReshipSummaryComponent
          combinedVehiclesByBundle={summaryBundleMap}
          selectedVehicleEntities={selectedVehicleEntities}
          onBack={handleSummaryBack}
          onContinue={handleSummaryContinue}
        />
      ) : (
        <>
          <Spacer top="10px" />
          <Breadcrumbs surface="light">
            <BreadcrumbItem key={0} active={false}>
              <span
                css={{
                  fontWeight: "400",
                  color: "black",
                }}
                onClick={() => {
                  handleChangePage({ index: 0 });
                }}
              >
                Return / Reship
              </span>
            </BreadcrumbItem>
            <BreadcrumbItem key={1} active={false}>
              <span
                css={{
                  fontWeight: "700",
                  color: "black",
                }}
              >
                Assign Materials
              </span>
            </BreadcrumbItem>
          </Breadcrumbs>
          <Spacer bottom="1rem" />
          <OrderShippingSummaryAccordion />
          <Loader active={isVehicleLoading} fullscreen />
          <div style={{ margin: "1rem 0", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "flex-end", width: "100%", gap: "1rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", maxWidth: "680px", flexGrow: 0 }}>
                <label style={{ fontWeight: 600, fontSize: "12px" }}>Selected Materials:</label>
                <BundleDropdownWrapper>
                  <BundleDropdownTrigger type="button" onClick={toggleBundleDropdown} aria-haspopup="listbox" aria-expanded={isBundleDropdownOpen}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {selectedMaterialDetails ? (
                        <SelectedMaterialLabel>
                          <strong>{`${selectedMaterialDetails.requestedQuantity} Requested | `}</strong>
                          {(() => {
                            const base = `${selectedMaterialDetails.hardwareName} (${selectedMaterialDetails.hardwareId}) | ${selectedMaterialDetails.subscriptionName} (${selectedMaterialDetails.subscriptionId})`;
                            const MAX_LEN = 90;
                            return base.length > MAX_LEN ? base.slice(0, MAX_LEN) + "..." : base;
                          })()}
                        </SelectedMaterialLabel>
                      ) : (
                        <SelectedMaterialLabel>Choose hardware item</SelectedMaterialLabel>
                      )}
                    </span>
                    <span style={{ fontSize: "0.7rem", flexShrink: 0 }}>{isBundleDropdownOpen ? "▲" : "▼"}</span>
                  </BundleDropdownTrigger>
                  {isBundleDropdownOpen && (
                    <BundleOptionsContainer role="listbox">
                      {aggregatedMaterials.map((item) => {
                        const key = `${item.hardwareId}|${item.subscriptionId}`;
                        const isSaved = savedMaterialKeys[key];
                        return (
                          <BundleOptionRow key={key} onClick={() => handleHardwareSelect(key)}>
                            {isSaved && <img src={CheckIcon} alt="saved" height="14" width="14" style={{ display: "inline-block" }} />}
                            <span>
                              <strong>{`${item.requestedQuantity} Requested | `}</strong>
                              {(() => {
                                const base = `${item.hardwareName} (${item.hardwareId}) | ${item.subscriptionName} (${item.subscriptionId})`;
                                const MAX_LEN = 120;
                                return base.length > MAX_LEN ? base.slice(0, MAX_LEN) + "..." : base;
                              })()}
                            </span>
                          </BundleOptionRow>
                        );
                      })}
                    </BundleOptionsContainer>
                  )}
                </BundleDropdownWrapper>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto", marginTop: "22px", alignItems: "flex-start" }}>
                <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }}>
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={handleBackClick}
                    aria-label="Back"
                    style={{ backgroundColor: "#ffffff", color: "#000", border: "1px solid #ccc" }}
                  >
                    Back
                  </Button>
                  <Button size="small" variant="primary" disabled={!isSaveEnabled} onClick={handleSaveAssignment} aria-label="Save Assignment">
                    Save Assignment
                  </Button>
                  <Button size="small" variant="secondary" disabled={!isContinueEnabled} onClick={handleContinueAll} aria-label="Continue">
                    Continue
                  </Button>
                  {overSelectedInfo && (
                    <span
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 10,
                        marginTop: "4px",
                        color: "#c00",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Selected {overSelectedInfo.selected} vehicles; requested quantity is {overSelectedInfo.requested}.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {selectedHardwareKey &&
            (() => {
              const [hId, sId] = selectedHardwareKey.split("|");
              const materialFilter = { hardwareId: hId, subscriptionId: sId };
              const vehiclesProp = Object.keys(combinedVehiclesByBundle).length ? combinedVehiclesByBundle : vehicleList;
              return (
                <div style={{ marginTop: "2rem" }}>
                  {(() => {
                    const selectionMeta = materialSelections[currentMaterialKey];
                    const isSaved = savedMaterialKeys[currentMaterialKey];
                    let filteredVehiclesProp = vehiclesProp;
                    if (isSaved && selectionMeta?.vehicleIds?.length) {
                      const selectedSet = new Set(selectionMeta.vehicleIds);
                      if (filteredVehiclesProp && typeof filteredVehiclesProp === "object" && !Array.isArray(filteredVehiclesProp)) {
                        const bundleFiltered = {};
                        Object.keys(filteredVehiclesProp).forEach((bid) => {
                          const groups = filteredVehiclesProp[bid] || [];
                          if (Array.isArray(groups) && groups.some((g) => g && Array.isArray(g.vehicles))) {
                            const newGroups = groups
                              .map((grp) => ({
                                ...grp,
                                vehicles: (grp.vehicles || []).filter((v) => selectedSet.has(v.id || v.vehicle)),
                              }))
                              .filter((grp) => grp.vehicles.length);
                            bundleFiltered[bid] = newGroups;
                          } else {
                            bundleFiltered[bid] = (groups || []).filter((v) => selectedSet.has(v.id || v.vehicle));
                          }
                        });
                        filteredVehiclesProp = bundleFiltered;
                      } else if (Array.isArray(filteredVehiclesProp)) {
                        filteredVehiclesProp = filteredVehiclesProp.filter((v) => selectedSet.has(v.id || v.vehicle));
                      }
                    }
                    if (
                      filteredVehiclesProp === null ||
                      (typeof filteredVehiclesProp === "object" &&
                        !Array.isArray(filteredVehiclesProp) &&
                        Object.keys(filteredVehiclesProp).length === 0)
                    ) {
                      return (
                        <div style={{ padding: "1rem", fontSize: "14px", color: "#444" }}>
                          No vehicle found for this material, exclude it to continue return/reship process
                        </div>
                      );
                    }
                    return (
                      <AssignMaterialVehicleGrid
                        bundleIds={activeBundleId ? [activeBundleId] : []}
                        addressId={props?.data?.addressId || props?.navigationData?.addressId || ""}
                        contactId={props?.data?.contactId || props?.navigationData?.contactId || ""}
                        crmRefId={JSON.parse(sessionStorage.getItem("CSPDetails"))?.CSP?.[0]?.CRM_Ref_ID || ""}
                        materialFilter={materialFilter}
                        vehicleList={filteredVehiclesProp}
                        activeBundleId={activeBundleId}
                        selectedMaterialDetails={selectedMaterialDetails}
                        materialKey={currentMaterialKey}
                        externalSelectedVehicleIds={selectionMeta?.vehicleIds || []}
                        onSelectionChange={handleVehicleSelectionChange}
                        selectionLocked={isSaved === true}
                      />
                    );
                  })()}
                </div>
              );
            })()}
        </>
      )}
      {commonModalStatus && (
        <WTCommonModal
          commonModalStatus={commonModalStatus}
          setCommonModalStatus={setCommonModalStatus}
          title={title}
          subTitle={subTitle}
          actionItem={actionItem}
          notify={notify}
          handleContactModal={handleContactModal}
          handleShipmentDamagedModal={handleShipmentDamagedModal}
        />
      )}

      {isConfirmationModal && actionName === "assignment" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          handleContinue={handleContinueAssigment}
          modalHeading="Confirmation"
          modalBody="You will not be able to make any changes after you save the assignment. Please make sure these are the vehicles you want assigned for the hardware. Otherwise, you will have to start the process over if you want to change a vehicle."
          actionName={actionName}
        />
      )}

      {isConfirmationModal && actionName === "contact" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          modalHeading="Return Label Contact"
          modalBody="Do you need a return label?"
          actionName={actionName}
          handleContinue={handleContinue}
          handleNo={handleNo}
        />
      )}

      {isConfirmationModal && actionName === "create_workticket" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          modalHeading="Return Ticket Confirmation"
          modalBody="Are you sure you want to proceed with creating a return ticket for a return or return and reship?"
          actionName={actionName}
          handleContinue={handleContinue}
        />
      )}

      {isConfirmationModal && actionName === "back" && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          setIsConfirmationModal={setIsConfirmationModal}
          handleContinue={handleBackContinue}
          modalHeading="Confirm Going Back"
          modalBody="All changes during the return/reship in the vehicle will be lost if you go back."
          actionName={actionName}
          continueButtonLabel="Continue"
        />
      )}

      {showReturnLabelModal && (
        <ReturnLabelContactModal
          setisShowReturnLabelModal={setShowReturnLabelModal}
          onClose={handleCloseModal}
          title="Return Label Contact"
          handleSaveData={handleSaveData}
        />
      )}
    </div>
  );
};

export default AssignMaterialComponent;

export const AssignMaterialComponentMetadata = {
  name: "Assign Material",
  id: "assign_material",
  component: lazy(() => import("./AssignMaterialComponent")),
  route: "/connect360/assign_material",
  Breadcrumbs: true,
};
